import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { NavItem } from '../../shared/components/DashUI'
import { Drawer } from '../../shared/components/Drawer'
import { IconX } from '../../shared/console/icons'
import { BP, useMediaQuery } from '../../shared/hooks/useMediaQuery'

/**
 * DashboardShell — every adult surface.
 *
 * Information-dense and scannable, WARM rather than corporate. The warmth
 * comes from rounded corners, generous spacing, warm neutrals and a rounded
 * typeface — never from cartoon elements (design-system.md §1). There is no
 * grey in the token set to reach for.
 *
 * Four navigation variants, one shell
 * (information-architecture.md §2):
 *
 *   sidebar   School (7 items) · SuperAdmin (6)  — standard admin, 2–3 clicks
 *   adaptive  Parent (6 items)                   — sidebar on a screen, thumb bar on a phone
 *   bottom    A thumb bar at every width         — kept for the specimen and any future tier
 *   flat      Teacher, after the D1 mode fork    — one slot, no hunting
 *
 * 🔴 The teacher fork is the important one. After login a teacher picks Play
 * Mode or Dashboard Mode and everything hangs off that single decision.
 * Merging them into one navigation costs taps during the 30-minute slot, which
 * is the moment adoption is won or lost — hence `flat`, which deliberately has
 * no persistent chrome to hunt through.
 *
 * ✅ `adaptive` EXISTS BECAUSE THE PARENT TIER IS READ ON BOTH, AND THE BAR WAS
 * SIZED FOR ONLY ONE OF THEM. Six items thumb-reachable across the bottom of a
 * 360px phone is right; the same six strung across 1360px of a laptop is a strip
 * of far-apart words at the one edge of the window a desktop reader never looks
 * at — with the wordmark and the tier label, which live in the sidebar, drawn
 * nowhere at all. So the item list is one list and the chrome that carries it
 * follows the viewport: a real left sidebar from `md` up, the thumb bar below
 * it. Neither tier loses the shape it was designed for.
 *
 * ── Three shapes, not four variants ───────────────────────────────────────
 *
 * ✅ EVERY VARIANT NOW HAS AN ANSWER FOR A NARROW WINDOW, AND `sidebar` WAS THE
 * ONE THAT DID NOT. It drew the same 248px column at every width — 69% of a
 * 360px phone, leaving 112px for a roster table. The header carried a hamburger
 * with no handler behind it, which is how the gap survived: the screen looked
 * like it had a way out.
 *
 *   variant     phone                tablet               laptop
 *   ────────    ─────────────────    ─────────────────    ─────────────────
 *   sidebar     drawer + hamburger   drawer + hamburger   static sidebar
 *   adaptive    thumb bar            static sidebar       static sidebar
 *   bottom      thumb bar            thumb bar            thumb bar
 *   flat        no nav               no nav               no nav
 *
 * The two hand-over points differ on purpose — `lg` for `sidebar`, `md` for
 * `adaptive` — and the reason is what each tier's widest surface is. See
 * `hasDrawer`.
 */
export type DashboardNav = 'sidebar' | 'bottom' | 'flat' | 'adaptive'

export interface DashboardShellProps {
  nav?: DashboardNav
  /** Sidebar or bottom-nav items. Ignored when `nav="flat"`. */
  navItems?: ReactNode
  /**
   * What the thumb bar draws, when it is not the whole of `navItems`.
   *
   * 🔴 SIX ITEMS DID NOT FIT A PHONE, AND THE BAR WAS THE TIER'S ONLY WAY OUT.
   * At 360–412px six equal cells are 54–60px each, so "Achievements" and
   * "Announcements" rendered as "Achievem…" and "Announce…", at 10px. The rest
   * go in `thumbMore`'s sheet. Absent, the bar draws `navItems` as before.
   */
  thumbItems?: ReactNode
  /**
   * The thumb bar's last slot — "More" — and the bottom sheet it opens.
   *
   * 🔴 THE SHEET IS WHERE A PHONE READER SIGNS OUT. `actions` is not drawn by
   * this shell, and the sidebar footer that carries sign-out is `md` and up — so
   * below `md` the parent tier had no sign-out anywhere on screen.
   *
   * ⚠️ IT IS RENDERED BESIDE THE BAR, NOT INSIDE IT. `play.css` restacks every
   * `.play-bar .play-nav` icon-over-label; rows inside the bar's subtree would be
   * drawn as six more thumb-bar cells.
   */
  thumbMore?: {
    label: string
    icon: ReactNode
    /** One of the sheet's destinations is the page on screen. */
    active: boolean
    closeLabel: string
    content: ReactNode
  }
  /** Sidebar header — the wordmark lives here (design-system.md §10). */
  brand?: ReactNode
  /**
   * Which tier this is, in two words, under the wordmark.
   *
   * 🔴 IT ANSWERS THE ONE QUESTION THE SIDEBAR COULD NOT. The operator tier and
   * the school tier render the same wordmark, the same warm sidebar and a list
   * of nouns — and "Schools", "Content", "Settings" read plausibly on either.
   * An operator who has just used X4 to act as a head teacher is one glance
   * from making a change in the wrong account, which is the exact mistake
   * admin-panel.md §8 is written about.
   */
  tier?: ReactNode
  /**
   * Artwork at the foot of the nav column, above the account controls.
   *
   * 🔴 IT EXISTS BECAUSE SIX ITEMS DO NOT FILL A SIDEBAR, and what was under them
   * was 300px of empty cream — which on a first look reads as a list that failed
   * to finish loading, not as generous spacing. It is `mt-auto` inside the
   * scrolling nav, so a tier with enough items simply pushes it out of view
   * rather than fighting them for room.
   *
   * ⚠️ DECORATION ONLY, AND THE CALLER MUST KEEP IT THAT WAY. Nothing here may
   * carry a fact, because the phone variant has no sidebar to draw it in.
   */
  sidebarArt?: ReactNode
  /**
   * Pinned to the bottom of the sidebar — the account controls.
   *
   * ⚠️ SEPARATED FROM `navItems` ON PURPOSE. Settings and sign-out are not
   * screens this tier is *for*; putting them in the same list as the work would
   * make an operator scan past "Sign out" every time they look for "Schools".
   */
  sidebarFooter?: ReactNode
  /**
   * ✅ THERE IS NO `title` PROP ANY MORE, AND ITS REMOVAL FIXED TWO THINGS AT ONCE.
   *
   * 🔴 IT WAS THE THIRD COPY OF THE SAME WORD. Open the platform tier and
   * "Platform" appeared in the sidebar (highlighted, `aria-current="page"`), in
   * this chrome bar, and again as the screen's own `PageHead` — which is the one
   * that carries the sentence explaining what the screen is for. A person who
   * has just clicked "Schools" in a sidebar that then highlights "Schools" does
   * not need to be told twice more.
   *
   * 🔴 AND IT WAS A SECOND `<h1>` ON EVERY PAGE. `PageHead` renders an `<h1>`,
   * and this bar rendered another one above it — so every dashboard shipped two
   * top-level headings. A screen-reader user heard the page title twice and the
   * document outline was wrong on every screen in the product. That is not a
   * style preference; one `<h1>` per document is what the outline algorithm is
   * built on.
   *
   * ⚠️ THE BAR ITSELF SURVIVES **ONLY FOR `actions`**, AND THAT IS LOAD-BEARING.
   * `nav="bottom"` (a parent on a phone) and `nav="flat"` draw no sidebar, so
   * this is where their Settings and Sign-out live — on a shared classroom
   * device, a sign-out that exists on some screens and not others is a session
   * left open for the next class. With `nav="sidebar"` no actions are passed, so
   * the bar is not rendered at all.
   */
  actions?: ReactNode
  /**
   * The name of the screen, in the chrome bar, where the wordmark is not drawn.
   *
   * 🔴 IT REPLACED THE WORD `Dashboard`, WHICH THIS FILE PRINTED AS A LITERAL ON
   * EVERY ADULT SCREEN IN THE PRODUCT. Removing the `title` PROP above was
   * right — it was a second `<h1>` — but the hardcoded string it left behind was
   * not the absence of a title, it was one wrong title everywhere: an admin on
   * Children, Teachers, Classes or Our plan read "Dashboard" above all four. The
   * bar was the one line on screen that could not be trusted, which is the same
   * defect as the dead hamburger and the dead search box recorded below.
   *
   * ⚠️ A `<span>`, NEVER AN `<h1>`, AND THAT IS THE HALF OF THE OLD PROP THAT
   * MUST NOT COME BACK. One `<h1>` per document is what the outline algorithm is
   * built on, and every screen's own `PageHead` renders it. This is chrome —
   * where you are — sitting above the page that says what it is for.
   *
   * ⚠️ A SLOT WITH NO FALLBACK. Pass nothing and nothing is drawn; a shell that
   * guessed a word for a caller that had not wired one is exactly how the
   * literal survived this long. `AppRouter` derives it from the route id via
   * `pageTitleFor`, so the bar and the sidebar say the same word by construction.
   *
   * ⚠️ IT FOLLOWS THE WORDMARK'S BREAKPOINTS AND SO IS DRAWN ONLY WHERE THE
   * SIDEBAR ALREADY IS. Below that the brand takes the slot and the screen's own
   * heading is the answer — a phone header carrying a hamburger, a wordmark and
   * a page name is three things in 360px, and the third is repeated 40px lower.
   */
  pageLabel?: ReactNode
  /**
   * The top-right notification control.
   *
   * 🔴 SEPARATE FROM `actions`, AND THE REASON IS THE `md:hidden` BELOW.
   * `actions` stands down at `md` under `adaptive`, because the sidebar footer
   * draws the same Settings and Sign out and two of each is a reader wondering
   * whether they differ. Nothing else draws the bell, so folding it into
   * `actions` would delete it from every desktop parent screen — the tier most
   * likely to be told something.
   */
  notifications?: ReactNode
  /**
   * The search control in the chrome bar, left of the bell.
   *
   * 🔴 A SLOT, AND IT REPLACED AN `<input>` THIS FILE DREW ITSELF WITH NOTHING
   * BEHIND IT. What is searchable differs per tier and depends on the router, so
   * a shell that owned the control could only ever own a dead one. Pass
   * `GlobalSearch`; pass nothing and no box is drawn, which is the honest state
   * for a caller that has not wired one.
   */
  search?: ReactNode
  /**
   * A full-width strip between the header and the page — X4's impersonation
   * banner, and nothing else so far.
   *
   * 🔴 IT IS FULL WIDTH BECAUSE IT IS ABOUT THE WHOLE SESSION, NOT THE PAGE. It
   * used to be a pill among the header's buttons, which is where a reader's eye
   * goes last and where it competed with "Settings" for attention. §8 requires
   * an impersonated session be *"visibly indicated"*; a chip beside a link is
   * technically visible and practically not.
   */
  /**
   * Optional custom container max-width class for the page content.
   * Defaults to `max-w-[96rem]` to provide generous spacing on wide monitors.
   */
  contentMaxWidthClass?: string
  banner?: ReactNode
  children: ReactNode
}

export function DashboardShell({
  nav = 'sidebar',
  navItems,
  thumbItems,
  thumbMore,
  brand,
  tier,
  sidebarArt,
  sidebarFooter,
  actions: _actions,
  pageLabel,
  notifications,
  search,
  banner,
  contentMaxWidthClass = 'max-w-[96rem]',
  children,
}: DashboardShellProps) {
  /*
   * ⚠️ ONE FLAG, READ IN THREE PLACES — the sidebar, the thumb bar and the
   * actions strip all have to agree about which of the two shapes is on screen,
   * and they agree by all asking the same question.
   */
  const adaptive = nav === 'adaptive'
  const hasSidebar = nav === 'sidebar' || adaptive
  const hasBottomBar = nav === 'bottom' || adaptive

  /*
   * 🔴 THE DRAWER BELONGS TO `sidebar` AND TO NOTHING ELSE, AND THE REASON IS
   * THAT THE OTHER THREE VARIANTS ALREADY ANSWERED THE NARROW CASE.
   *
   * `adaptive` has the thumb bar below `md`; `bottom` has it at every width;
   * `flat` has no navigation by design. Only `sidebar` — the school and operator
   * consoles — had a single shape for every viewport, and that shape is a 248px
   * column. On the 360px phone a head teacher opens a message on, that column is
   * 69% of the window and the roster it covers gets 112px. There is no width it
   * could shrink to that leaves both usable, so below `lg` it stops being a
   * column and becomes a drawer.
   *
   * ⚠️ `lg` RATHER THAN `md`, AND THE TABLET IS THE REASON. These two tiers are
   * the table-heavy ones — a roster carries roll, name, parent, phone and a date.
   * At `md` (768px) the static sidebar leaves 520px for five columns, which fits
   * only by scrolling the table sideways on a device that has the room to show
   * it. The parent tier keeps its sidebar from `md` because its widest surface is
   * a stat grid, which reflows.
   */
  const hasDrawer = nav === 'sidebar'
  const [drawerOpen, setDrawerOpen] = useState(false)
  const closeDrawer = useCallback(() => { setDrawerOpen(false) }, [])

  /*
   * 🔴 THE ONE THING A CLASS COULD NOT DO. `lg:hidden` on the panel would hide a
   * drawer that is still OPEN — leaving the scroll lock applied, focus trapped
   * inside an invisible dialog and the page underneath unreachable, on a window
   * that looks entirely normal. Widening past the breakpoint has to CLOSE it,
   * because the sidebar it stands in for has just come back.
   */
  const desktop = useMediaQuery(BP.lg)
  useEffect(() => {
    if (desktop) setDrawerOpen(false)
  }, [desktop])

  /* The thumb bar's sheet, and the same rule for the same reason: the bar is
     `md:hidden` under `adaptive`, so widening past `md` has to close it. */
  const [moreOpen, setMoreOpen] = useState(false)
  const closeMore = useCallback(() => { setMoreOpen(false) }, [])
  const tablet = useMediaQuery(BP.md)
  useEffect(() => {
    if (tablet && adaptive) setMoreOpen(false)
  }, [tablet, adaptive])

  /*
   * The sidebar's contents, written once and mounted twice — as the static
   * column on a laptop, and inside the drawer on everything narrower.
   *
   * 🔴 IT IS A FUNCTION RATHER THAN AN ELEMENT BECAUSE THE TWO MOUNTS MUST NOT
   * SHARE `id`s OR `aria-current` STATE. Both are in the document at the same
   * time only across a resize, but React reconciles them as separate subtrees
   * either way; a single element reference reused in two places is one subtree
   * React moves rather than two it keeps, and the drawer would steal the
   * sidebar's DOM nodes — and with them the focus inside it.
   *
   * ⚠️ `inDrawer` EXISTS FOR THE ARTWORK, WHICH THE DRAWER MUST NOT DRAW. The
   * sidekick and the schoolhouse are `display: none` under 48rem already
   * (play.css), so on a phone the drawer would reserve a box and paint nothing.
   */
  const sidebarBody = (inDrawer: boolean) => (
    <>
      {brand || tier ? (
        <div className="flex shrink-0 items-center gap-2.5 pt-3.5 pr-3 pb-4 pl-4.5">
          {brand}
          {tier ? (
            <p className="inline-flex shrink-0 rounded-[6px] border border-line bg-surface-muted px-1.5 font-dash text-[11.5px] leading-4 font-medium text-ink-soft">
              {tier}
            </p>
          ) : null}
        </div>
      ) : null}
      {/*
        ⚠️ THE NAV SCROLLS, THE FOOTER DOES NOT. Six items fit a laptop today;
        a seventh on a 768px window with the browser's own chrome would push
        sign-out off the bottom of a shared classroom machine, which is the one
        control that must never be hunted for.
      */}
      <nav
        /*
         * 🔴 THE DELEGATED CLICK IS HOW THE DRAWER CLOSES ON NAVIGATION, AND
         * THERE IS NO OTHER PLACE TO PUT IT. `navItems` is an opaque `ReactNode`
         * handed in by six different tiers — this shell cannot reach into it to
         * wrap each item's own `onClick`, and cloning children would break the
         * moment a tier passes a `NavGroup` instead of a flat list.
         *
         * ⚠️ IT FIRES ON ANY CLICK INSIDE THE LIST, INCLUDING ONE THAT MISSES AN
         * ITEM. That is the right failure: a tap on the gap between two rows
         * closing the menu is a reader dismissing it, not a bug. The opposite —
         * a drawer still covering the screen after the page behind it changed —
         * is the one every phone reader would report.
         */
        onClick={inDrawer ? closeDrawer : undefined}
        className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-2.5 pb-4"
      >
        {navItems}
      </nav>
      {/*
        🔴 OUTSIDE THE SCROLLING NAV, AND THAT IS A FIX RATHER THAN A TIDY-UP.
        It was `mt-auto` INSIDE `<nav>`, which is `overflow-y-auto` — so on a
        682px-tall window the artwork pushed the item list past its own
        height, put a scrollbar down the sidebar, and got itself clipped in
        half. Decoration must never be able to make navigation scroll.

        As a sibling it takes only what is left after the nav, and `play.css`
        hides it outright on a window too short to have any. `shrink-0` on
        the footer below still wins, so sign-out is never crowded.
      */}
      {sidebarArt && !inDrawer ? (
        <div className="shrink-0 px-3 pb-2" aria-hidden="true">
          {sidebarArt}
        </div>
      ) : null}
      {sidebarFooter ? (
        <div
          /*
           * ⚠️ `safe-pb-3` ONLY IN THE DRAWER — and it IS `pb-3`, plus the
           * inset, rather than an extra class beside it. The static sidebar is inside an
           * `h-dvh` row on a laptop, where the inset is 0 and the class is
           * inert — but the drawer is `fixed inset-y-0`, so on a notched phone
           * held upright its sign-out button lands under the home indicator.
           */
          className={`shrink-0 border-t border-line px-3 pt-3 ${inDrawer ? 'safe-pb-3' : 'pb-3'}`}
          onClick={inDrawer ? closeDrawer : undefined}
        >
          {sidebarFooter}
        </div>
      ) : null}
    </>
  )

  const content = (
    <div className="flex min-h-0 flex-1 flex-col">
      {/*
        The chrome bar.

        ⚠️ EVERY MEASUREMENT IN IT IS A THREE-STEP RAMP, NOT ONE NUMBER. `px-8`
        at 360px spends 64 of the 360 available on nothing, which is the width of
        the roll-number column it then has to clip. `h-16` is 88px of a 640px
        phone viewport once the URL bar is counted — a seventh of the screen,
        above the fold, carrying a search box that is hidden at that width.
      */}
      {/*
        🔴 `print:hidden` — THE CHROME IS NAVIGATION, AND ON PAPER IT IS A PAGE
        NAME, A DEAD SEARCH BOX AND A BELL ACROSS THE TOP OF SOMEBODY'S
        DOCUMENT. `print.css` already removes the sidebar, the thumb bar and the
        account toolbar (`aside, nav, [role='toolbar']`) for exactly this
        reason, and this bar was the one piece of chrome not covered — it is a
        `<header>`, and that selector cannot be added there because `PageHead`
        and every printed sheet use the same element for the page's own heading.
        It showed up on both documents the product prints: C2's parent report and
        E8's child profile.
      */}
      <header className="z-10 flex h-14 shrink-0 items-center justify-between gap-2 border-b border-line bg-canvas px-4 sm:px-6 lg:px-8 print:hidden">
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          {hasDrawer ? (
            <button
              type="button"
              onClick={() => { setDrawerOpen(true) }}
              aria-label="Open navigation menu"
              aria-expanded={drawerOpen}
              aria-haspopup="dialog"
              /*
               * 🔴 THIS BUTTON EXISTED BEFORE AND DID NOTHING — no handler, no
               * state, no drawer to open. It was drawn `md:hidden`, so on every
               * phone the school and operator tiers shipped a hamburger that a
               * reader could press as many times as they liked. A control that
               * looks like the way out and is not is worse than no control:
               * the reader stops looking for the real one.
               */
              className="-ml-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-muted transition-colors hover:bg-surface-muted hover:text-ink lg:hidden"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          ) : null}

          {/*
            🔴 THE WORDMARK MOVES INTO THE HEADER EXACTLY WHERE THE SIDEBAR
            STOPS DRAWING IT, AND WITHOUT THIS IT WAS DRAWN NOWHERE. `brand`
            lives in the sidebar; below that sidebar's breakpoint the product
            rendered a bar reading "Dashboard" over an unbranded page, on the
            surfaces most likely to be someone's first look at it. The
            breakpoint differs per variant because the sidebar's does — `lg` for
            the drawer tiers, `md` for the parent tier's thumb bar, always for
            the two that never draw one.
          */}
          {brand ? (
            <div
              className={`flex min-w-0 items-center ${
                hasDrawer ? 'lg:hidden' : adaptive ? 'md:hidden' : ''
              }`}
            >
              {brand}
            </div>
          ) : null}

          {pageLabel ? (
            <span
              /*
               * Hidden wherever the wordmark above is shown — two brand blocks in
               * one bar is a reader working out whether they mean different things.
               */
              className={`truncate font-dash text-dash-sm font-medium text-ink ${
                brand ? (hasDrawer ? 'hidden lg:inline' : adaptive ? 'hidden md:inline' : 'hidden') : ''
              }`}
            >
              {pageLabel}
            </span>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-4 lg:gap-5">
          {/*
            The search box, supplied by the caller.

            🔴 IT USED TO BE DRAWN HERE, INLINE, WITH NO HANDLER ON IT — an
            `<input type="search" placeholder="Search anything...">` and nothing
            else, on every adult screen in the product. It is the same defect as
            the hamburger above: a control that looks like a way through and is
            not teaches the reader to stop looking for the real one.

            ⚠️ A SLOT RATHER THAN A PROP OF DATA, because what is searchable is a
            tier's business and not this component's. `GlobalSearch` is what goes
            in it and `AppRouter` builds the list from `navFor(role)`, which is
            what makes the results role-scoped by construction.

            ⚠️ NOTHING RENDERS WHEN NOTHING IS PASSED. A shell that fell back to
            the dead input for a caller that had not wired one yet would be the
            original bug with a longer life.
          */}
          {search}

          {/* Notifications */}
          <div className="relative flex shrink-0 cursor-pointer items-center">
            {notifications}
          </div>
        </div>
      </header>

      {banner ? <div className="shrink-0">{banner}</div> : null}
      {/*
        The page.

        ⚠️ THE INSET RAMPS AND THE MAX-WIDTH DOES NOT. 32px either side is right
        on the laptop the design was drawn for and is 18% of a 360px phone spent
        on margin; 16px is the phone's own figure. The `73.75rem` cap is a
        reading-measure limit and has nothing to do with the device — it is
        already inert on anything narrower than itself.

        ⚠️ THE LANDSCAPE NOTCH IS NOT HANDLED HERE. It is handled once on the
        shell root below (`safe-px`), which insets the sidebar and the page
        together. Putting it here as well would be the collision `responsive.css`
        documents at length — a plain class beating `px-4` and resolving to zero.
      */}
      <div className="min-h-0 flex-1 overflow-auto px-4 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-6">
        <div className={`mx-auto w-full ${contentMaxWidthClass}`}>{children}</div>
      </div>
    </div>
  )

  if (nav === 'flat') {
    return (
      <div className="console safe-px flex h-dvh flex-col overflow-hidden bg-canvas font-dash text-ink">
        {content}
      </div>
    )
  }

  return (
    /*
     * ⚠️ `safe-px` ON THE ROOT, WHICH IS THE ONE BOX IN THIS SHELL WITH NO
     * HORIZONTAL PADDING OF ITS OWN. A phone held sideways puts the notch on a
     * long edge; without this the sidebar's first letter and every table row's
     * first character sit under it. On every device without one it is 0 and
     * costs nothing.
     */
    <div className="console safe-px flex h-dvh overflow-hidden bg-canvas font-dash text-ink">
      {hasSidebar ? (
        <aside
          /*
           * ⚠️ THE BREAKPOINT IS THE VARIANT'S, NOT THE SHELL'S. `adaptive`
           * hands over to a thumb bar at `md`; `sidebar` hands over to the
           * drawer at `lg`. One number for both would either give the parent
           * tier a drawer it does not need or leave the admin tiers a column
           * on a tablet that has no room for it.
           */
          className={`${adaptive ? 'hidden md:flex' : 'hidden lg:flex'} w-[15.25rem] shrink-0 flex-col border-r border-line bg-surface-muted`}
        >
          {sidebarBody(false)}
        </aside>
      ) : null}

      {hasDrawer ? (
        <Drawer open={drawerOpen} onClose={closeDrawer} label="Navigation">
          {sidebarBody(true)}
        </Drawer>
      ) : null}
      {/*
        The page column. It is its own flex column so the thumb bar can sit
        BESIDE the sidebar rather than under it — a bar spanning the full window
        width would run past the bottom of the sidebar and read as a footer for
        the whole shell rather than navigation for the page.
      */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {content}
        {hasBottomBar ? (
          /*
            Thumb-reachable. The tier's items, or four and "More" when `thumbMore`
            is given — a parent has one job here.

            ⚠️ `[&>*]:w-auto` UNDOES THE NAV ITEM'S OWN `w-full`. The same
            component draws the sidebar, where filling the column is right, and
            this bar, where it would make one item span the whole phone and six
            items fight for the same 100%.

            ⚠️ `safe-pb-2` REPLACES `py-2` ON THE BOTTOM EDGE, AND IT IS THE ONE
            PLACE IN THE PRODUCT `env()` IS LOAD-BEARING. This bar is the last
            element in an `h-dvh` column, so on any phone with a home indicator
            its labels sat UNDER the indicator: six taps that each land on the
            OS gesture area rather than on the item. `viewport-fit=cover` in
            `index.html` is what makes the inset readable at all.

            ⚠️ `min-w-0` ON THE ITEMS. Six labels across 320px is 53px each, and
            without it a long one ("Announcements") sets the flex basis and
            pushes the sixth item off the bar entirely.
          */
          <nav
            /* `play-bar` restacks the item icon-over-label and shrinks its disc —
               six sidebar-width rows would not fit across a 360px phone. */
            className={`play-bar ${adaptive ? 'md:hidden ' : ''}safe-pb-2 flex shrink-0 items-center justify-around gap-1 border-t border-line bg-surface px-2 pt-2 [&>*]:min-w-0 [&>*]:w-auto`}
          >
            {thumbItems ?? navItems}
            {thumbMore ? (
              <NavItem
                icon={thumbMore.icon}
                active={thumbMore.active}
                opensSheet={moreOpen}
                onClick={() => { setMoreOpen(true) }}
              >
                {thumbMore.label}
              </NavItem>
            ) : null}
          </nav>
        ) : null}
        {hasBottomBar && thumbMore ? (
          <Drawer open={moreOpen} onClose={closeMore} label={thumbMore.label} side="bottom">
            <span aria-hidden="true" className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-line-strong" />
            <div className="flex shrink-0 items-center justify-between gap-2 pt-1 pr-2 pl-5">
              <p className="font-dash text-[15px] leading-5 font-semibold text-ink">{thumbMore.label}</p>
              <button
                type="button"
                onClick={closeMore}
                aria-label={thumbMore.closeLabel}
                className="inline-flex h-[44px] w-[44px] items-center justify-center rounded-lg text-muted hover:bg-surface-muted hover:text-ink"
              >
                <IconX size={18} />
              </button>
            </div>
            {/* Delegated, like the drawer's nav: any row closes the sheet. */}
            <div className="play-sheet flex flex-col gap-0.5 px-2.5 pt-1 pb-2" onClick={closeMore}>
              {thumbMore.content}
            </div>
          </Drawer>
        ) : null}
      </div>
    </div>
  )
}

export default DashboardShell
